import { useCallback, useSyncExternalStore } from "react";

const KEY = "marloo.hiddenDashboards";

function read(): Set<string> {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr.filter((x) => typeof x === "string") : []);
  } catch {
    return new Set();
  }
}

const listeners = new Set<() => void>();
function subscribe(cb: () => void) {
  listeners.add(cb);
  const onStorage = (e: StorageEvent) => {
    if (e.key === KEY) cb();
  };
  window.addEventListener("storage", onStorage);
  return () => {
    listeners.delete(cb);
    window.removeEventListener("storage", onStorage);
  };
}
function emit() {
  listeners.forEach((l) => l());
}

let cache: Set<string> | null = null;
let cacheVersion = 0;
function getSnapshot(): number {
  if (cache === null) cache = read();
  return cacheVersion;
}
function getHiddenSet(): Set<string> {
  if (cache === null) cache = read();
  return cache;
}

function write(set: Set<string>) {
  cache = set;
  cacheVersion++;
  localStorage.setItem(KEY, JSON.stringify(Array.from(set)));
  emit();
}

export function useHiddenDashboards() {
  useSyncExternalStore(subscribe, getSnapshot, () => 0);
  const hidden = getHiddenSet();

  const isHidden = useCallback((id: string) => hidden.has(id), [hidden]);
  const setHidden = useCallback((id: string, value: boolean) => {
    const next = new Set(getHiddenSet());
    if (value) next.add(id);
    else next.delete(id);
    write(next);
  }, []);
  const toggle = useCallback((id: string) => {
    const next = new Set(getHiddenSet());
    if (next.has(id)) next.delete(id);
    else next.add(id);
    write(next);
  }, []);

  return { hidden, isHidden, setHidden, toggle };
}
